<script>
import Vue from 'vue'
import Component from 'vue-class-component'
import { mapGetters } from 'vuex'
import axios from 'axios'

import store from '@/store/index'
import EventBus from '@/event-bus'

import CompletedObservationStats from '@/components/admin/CompletedObservationStats'
import CaseloadDoughnut from '@/components/admin/CaseloadDoughnut'
import RecentObservations from '@/components/admin/RecentObservations'
import CaseloadTable from './CaseloadTable'
import SchoolsDropdown from '@/components/admin/SchoolsDropdown'
import CaseloadChartContainer from '@/components/admin/CaseloadChartContainer'

import { onboardingHelper } from '@/helpers/onboardingHelper'
import { caseloadHelper } from '@/helpers/caseloadHelper'

const apiBase = API_APP_PATH + "/v1"

@Component({
    components: {
        CompletedObservationStats,
        CaseloadDoughnut,
        RecentObservations,
        CaseloadTable,
        SchoolsDropdown,
        CaseloadChartContainer
    },
    computed: {
        ...mapGetters({
            fiscalYear: 'getFiscalYear',
            selectedSchool: 'admin/getSelectedSchool',
            schools: 'admin/getSchools',
            userRole: 'getUserRole'
        }),
        completedObservations() {
            let teachers = this.selectedSchool.teachers.filter(teacher => 
                teacher.FiscalYear === this.fiscalYear
            )
            return teachers.reduce((previous, current) => 
                previous + current.observation_N, 0
            )
        },
        expectedObservations() {
            let teachers = this.selectedSchool.teachers.filter(teacher => 
                teacher.FiscalYear === this.fiscalYear
            )
            return teachers.reduce((previous, current) => 
                previous + current.expected_obs, 0
            )
        },
        remainingObservations() {
            let teachers = this.selectedSchool.teachers.filter(teacher => 
                teacher.FiscalYear === this.fiscalYear
            )
            return teachers.reduce((previous, current) => 
                previous + current.remaining_obs, 0
            )
        },
        selectedObservations() {
            return this.selectedSchool.observations.filter(o => o.FiscalYear === this.fiscalYear)
        },
        componentsDisplayed() {
            const components = this.selectedObservations.map(observation => 
                observation.componentsMeasured
            )
            return [].concat(...components)
        },
        teachersDisplayed() {
            return this.selectedSchool.teachers.filter(teacher => 
                teacher.FiscalYear === this.fiscalYear
            ).sort(function(a, b) {
                return a.LastName < b.LastName ? -1 : 1
            })
        },
        observationsToDatePercentage(){
            return ((this.completedObservations/this.expectedObservations)*100).toFixed(0)
        }
    }
})

export default class CaseloadManager extends Vue {
    steps = caseloadHelper().steps

    beforeRouteEnter(to, from, next) {
        let school = store.getters['admin/getSelectedSchool']
        axios.get(
            `${apiBase}/data/school/motp/components/${school.dbn}`
        ).then( ({data}) => {
            school.observations = school.observations.map(observation => {
                observation.componentsMeasured = data.filter(component => 
                    component.MOTPID === observation.MOTPID
                )
                observation.componentsMeasured = observation.componentsMeasured.map(component => {
                    component.Years_Of_Service = observation.Years_Of_Service
                    component.Grade_Level_Desc = observation.Grade_Level_Desc
                    component.Subject_Desc = observation.Subject_Desc
                    return component
                })
                return observation
            })
            store.commit("admin/updateSelectedSchool", school)
            next()
        }).catch((error) => {
            console.log(error)
        })
    }

    mounted() {
        EventBus.$on('start intro', () => {
            this.startIntro()
        })
        
        this.$nextTick(() => {
            if (localStorage.getItem('doneAdminCaseloadOnboarding')) {
                return
            } else {
                this.startIntro()
            }
        })
    }

    startIntro() {
        onboardingHelper().startIntro(
            introJs(), this, 'doneAdminCaseloadOnboarding'
        )
    }
}
</script>

<template>

    <div>

        <div class="caseload-title flex">
            <h1>
                Caseload Manager
                <span>
                    {{selectedSchool.name}}
                </span>
            </h1>
            <!-- <div class="filters school-filter">
                <span>School</span>
                <SchoolsDropdown v-if="userRole !== 'Teacher' && selectedSchool.dbn" 
                    :schools="schools"/>
            </div> -->
        </div>

        <div class="caseload-container">

            <div class="chart-and-stats container">
                <CaseloadChartContainer
                    :selectedObservations="selectedObservations"
                    :completedObservations="completedObservations"
                    :expectedObservations="expectedObservations"
                    :selectedSchool="selectedSchool"
                    :fiscalYear="fiscalYear"
                    :observationsToDatePercentage="observationsToDatePercentage" />
                <CompletedObservationStats
                    :completedObservations="completedObservations"
                    :selectedObservations="selectedObservations"
                    :remainingInYear="remainingObservations "/>
            </div>

            <div class="chart-and-recent container">
                <CaseloadDoughnut 
                    :completedObservations="completedObservations"
                    :expectedObservations="expectedObservations"
                    :observationsToDatePercentage="observationsToDatePercentage" />
                <RecentObservations
                    :completedObservations="completedObservations"
                    :selectedObservations="selectedObservations"
                    :teachersDisplayed="teachersDisplayed"
                    :userRole="userRole" />
            </div>
            
            <CaseloadTable :teachersDisplayed="teachersDisplayed"
                :selectedSchool="selectedSchool"
                :selectedObservations="selectedObservations"
                :componentsDisplayed="componentsDisplayed" />

        </div>

    </div>
    
</template>

<style lang="scss" scoped>
    div {
        .caseload-title {
            margin-top: 25px;
            align-items: center;
            h1 {
                font-size: 48px;
                text-transform: uppercase;
                color: #613f75;
                display: flex;
                align-items: center;
                span {
                    width: 500px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    margin-left: 25px;
                    font-size: 16px;
                    color: #4a4a4a;
                    line-height: normal;
                }
            }
            .filters {
                font-family: 'museo_sans', sans-serif;
                margin-top: -20px;
                span {
                    display: block;
                    margin: 0 0 5px;
                    color: #4a4a4a;
                    font-size: 14px;
                }
            }
        }
        .caseload-container {
            max-width: 1018px;
            margin: 0 auto;
        }
        span {
            margin: 0 40px 0 25px;
            font-size: 24px;
            color: #4a4a4a;
        }
        .container {
            display: flex;
            justify-content: space-between;
            &.chart-and-stats {
                border: 3px solid #979797;
                margin-top: 75px;
                margin-bottom: 45px;
                padding: 27px 54px;
            }
            &.chart-and-recent {
                h2 {
                    font-family: 'museo_sans';
                }
            }
        }
        
    }
</style>